import { combineReducers } from 'redux';
import omit from 'lodash/fp/omit';
import update from 'lodash/fp/update';
import pipe from 'lodash/fp/pipe';
import filter from 'lodash/fp/filter';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
import assoc from 'lodash/fp/assoc';
import keys from 'lodash/fp/keys';
import map from 'lodash/fp/map';
import fromPairs from 'lodash/fp/fromPairs';

import * as Actions from './actions';
import { flattenedTree } from '../../lib/outline-manipulation';
import { moveItem } from '../../lib/helpers';

const treeIntoFlags = pipe(
  flattenedTree,
  keys,
  map((key) => [key, true]),
  fromPairs
);

const removeChild = (componentId, parentId) =>
  update(
    [parentId, 'children'],
    filter((childId) => childId !== componentId)
  );

const deleteComponent = (componentId, tree) =>
  pipe(
    omit(componentId),
    removeChild(componentId, get(tree, [componentId, 'parentId']))
  )(tree);

const moveComponent = ({ movingId, siblingId }, state) => {
  const oldParentId = get(state, [movingId, 'parentId']);
  const newParentId = get(state, [siblingId, 'parentId']);

  return pipe(
    // update parentId
    assoc([movingId, 'parentId'], newParentId),

    // remove from previous parent
    update(
      [oldParentId, 'children'],
      filter((child) => child !== movingId)
    ),

    // place the component id into its new parent
    update([newParentId, 'children'], (children = []) =>
      moveItem(
        children.concat(movingId),
        children.length,
        children.findIndex((id) => siblingId === id)
      )
    )
  )(state);
};

export const flatOutline = (state = {}, action) => {
  const { component, order = Infinity, type, id } = action;

  switch (type) {
    case Actions.OUTLINE_RECEIVED:
      return flattenedTree(action.outline);
    case Actions.COMPONENT_UPDATED:
      return {
        ...state,
        [component.id]: component,
      };
    case Actions.COMPONENT_DELETED:
      return deleteComponent(id, state);
    case Actions.COMPONENT_ADDED:
      return {
        ...state,

        // add component
        [component.id]: component,

        // inform parent
        [component.parentId]: {
          ...state[component.parentId],

          // splice in new component's id
          children: [
            ...(state[component.parentId].children || []).slice(0, order),
            component.id,
            ...(state[component.parentId].children || []).slice(order),
          ],
        },
      };
    case Actions.ADD_REPEATABLE:
      return assoc(
        action.path,
        [
          ...get(state, action.path, []),
          { $unique_key: new Date().getTime(), ...action.repeatable },
        ],
        state
      );
    case Actions.MOVE_REPEATABLE:
      return assoc(
        action.path,
        moveItem(get(state, action.path, []), action.fromIndex, action.toIndex),
        state
      );
    case Actions.DELETE_REPEATABLE:
      return assoc(
        action.path,
        get(state, action.path).filter((x, i) => i !== action.index),
        state
      );
    default:
      return state;
    case Actions.MOVE_COMPONENT:
      return moveComponent(action, state);
  }
};

const builderLoaded = (state = false, action) => {
  switch (action.type) {
    case Actions.BUILDER_LOADED:
      return true;
    default:
      return state;
  }
};

const selected = (state = '', action) => {
  switch (action.type) {
    case Actions.COMPONENT_SELECTED:
      return action.id;
    case Actions.COMPONENT_ADDED:
      return action.component.id;
    case Actions.COMPONENT_DELETED:
      return action.id === state ? '' : state;
    case Actions.EDITOR_CLOSE:
      return '';
    default:
      return state;
  }
};

const builderVersion = (state = '', action) => {
  switch (action.type) {
    case Actions.SET_BUILDER_VERSION:
      return action.version;
    default:
      return state;
  }
};

const initialEditor = {
  open: false,
  tab: 0,
  width: 350,
  height: 460,
  dock: 'left',
};

const editor = (state = initialEditor, action) => {
  const { tab } = action;
  switch (action.type) {
    case Actions.EDITOR_OPEN:
      return {
        ...state,
        open: true,
      };
    case Actions.EDITOR_CLOSE:
    case Actions.COMPONENT_DELETED:
      return {
        ...state,
        open: false,
      };
    case Actions.EDITOR_TAB_SELECT:
      return {
        ...state,
        tab,
      };
    case Actions.COMPONENT_SELECTED:
    case Actions.COMPONENT_ADDED:
      return {
        ...state,
        tab: 1,
      };
    case Actions.EDITOR_UPDATE_HEIGHT:
      return action.height >= 20
        ? {
            ...state,
            height: action.height,
          }
        : state;
    case Actions.EDITOR_UPDATE_WIDTH:
      return action.width >= 350
        ? {
            ...state,
            width: action.width,
          }
        : state;
    case Actions.EDITOR_UPDATE_DOCK:
      return action.dock === state.dock
        ? {
            ...state,
            open: false,
          }
        : {
            ...state,
            dock: action.dock,
          };
    default:
      return state;
  }
};

const badges = (state = {}, action) => {
  switch (action.type) {
    case Actions.OUTLINE_RECEIVED:
      return treeIntoFlags(action.outline);
    case Actions.COMPONENT_ADDED:
      return {
        ...state,
        [action.component.id]: true,
      };
    case Actions.TOGGLE_BADGE:
      return {
        ...state,
        [action.componentId]: !state[action.componentId],
      };
    case Actions.CLOSE_ALL_BADGES:
      return {};
    default:
      return state;
  }
};

const watchQueryParams = (state = false, action) => {
  switch (action.type) {
    case Actions.SHOULD_WATCH_QUERY_PARAMS:
      return action.watchQueryParams;
    default:
      return state;
  }
};

const alwaysRenderInBuilder = (state = [], action) => {
  switch (action.type) {
    case Actions.ALWAYS_RENDER_IN_BUILDER:
      return state.concat([action.id]);
    default:
      return state;
  }
};

const sorting = (state = {}, action) => {
  switch (action.type) {
    case Actions.START_SORTING:
      return {
        ...state,
        componentId: action.componentId,
      };
    case Actions.STOP_SORTING:
      return {
        ...state,
        componentId: undefined,
      };
    case Actions.MOVE_COMPONENT:
      return {
        ...state,
        componentId: undefined,
      };
    default:
      return state;
  }
};

export default combineReducers({
  flatOutline,
  builderLoaded,
  selected,
  editor,
  badges,
  watchQueryParams,
  alwaysRenderInBuilder,
  sorting,
  version: builderVersion,
});
